import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_URL = `${BACKEND_URL}/api`;
export const STATIC_URL = BACKEND_URL;

const api = axios.create({ baseURL: API_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("smm_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Resolve image url (backend returns /api/static/uploads/... for new uploads)
// Backward-compat: old DB entries may still have /static/uploads/... — rewrite them so
// the production ingress (which only routes /api/* to backend) can fetch them.
export const resolveImg = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/static/uploads/")) {
    return `${BACKEND_URL}/api${url}`;
  }
  return `${BACKEND_URL}${url}`;
};

export default api;

// Auth
export const authAPI = {
  register: (data) => api.post("/auth/register", data).then(r => r.data),
  registerBusiness: (data) => api.post("/auth/register-business", data).then(r => r.data),
  login: (data) => api.post("/auth/login", data).then(r => r.data),
  me: () => api.get("/auth/me").then(r => r.data),
  googleSession: (session_id) => api.post("/auth/google/session", { session_id }).then(r => r.data),
  submitVerification: (doc_url, doc_type = "id_card") => api.post("/auth/me/verify", { doc_url, doc_type }).then(r => r.data),
};

// Public users
export const usersAPI = {
  public: (uid) => api.get(`/users/${uid}/public`).then(r => r.data),
};

// Listings
export const listingsAPI = {
  list: (params) => api.get("/listings", { params }).then(r => r.data),
  featured: () => api.get("/listings/featured").then(r => r.data),
  get: (id) => api.get(`/listings/${id}`).then(r => r.data),
  create: (data) => api.post("/listings", data).then(r => r.data),
  update: (id, data) => api.put(`/listings/${id}`, data).then(r => r.data),
  remove: (id) => api.delete(`/listings/${id}`).then(r => r.data),
  byUser: (uid) => api.get(`/users/${uid}/listings`).then(r => r.data),
};

// Offers
export const offersAPI = {
  create: (listingId, data) => api.post(`/listings/${listingId}/offers`, data).then(r => r.data),
  listForListing: (listingId) => api.get(`/listings/${listingId}/offers`).then(r => r.data),
  accept: (oid) => api.post(`/offers/${oid}/accept`).then(r => r.data),
  reject: (oid) => api.post(`/offers/${oid}/reject`).then(r => r.data),
};

// Barters
export const bartersAPI = {
  create: (listingId, data) => api.post(`/listings/${listingId}/barters`, data).then(r => r.data),
  listForListing: (listingId) => api.get(`/listings/${listingId}/barters`).then(r => r.data),
  accept: (bid) => api.post(`/barters/${bid}/accept`).then(r => r.data),
  reject: (bid) => api.post(`/barters/${bid}/reject`).then(r => r.data),
};

// Auctions
export const auctionsAPI = {
  list: () => api.get("/auctions").then(r => r.data),
  get: (id) => api.get(`/auctions/${id}`).then(r => r.data),
  create: (data) => api.post("/auctions", data).then(r => r.data),
  bid: (aid, amount) => api.post(`/auctions/${aid}/bid`, { amount }).then(r => r.data),
  buyNow: (aid) => api.post(`/auctions/${aid}/buy-now`).then(r => r.data),
};

// Categories
export const categoriesAPI = {
  list: () => api.get("/categories").then(r => r.data),
};

// Admin
export const adminAPI = {
  stats: () => api.get("/admin/stats").then(r => r.data),
  users: () => api.get("/admin/users").then(r => r.data),
  deleteUser: (uid) => api.delete(`/admin/users/${uid}`).then(r => r.data),
  listings: () => api.get("/admin/listings").then(r => r.data),
  deleteListing: (lid) => api.delete(`/admin/listings/${lid}`).then(r => r.data),
  toggleFeature: (lid) => api.post(`/admin/listings/${lid}/feature`).then(r => r.data),
  auctions: () => api.get("/admin/auctions").then(r => r.data),
  offers: () => api.get("/admin/offers").then(r => r.data),
  barters: () => api.get("/admin/barters").then(r => r.data),
  conversations: () => api.get("/admin/conversations").then(r => r.data),
  convMessages: (cid) => api.get(`/admin/conversations/${cid}/messages`).then(r => r.data),
  cleanupDemo: (deleteUsers = false) => api.post(`/admin/cleanup-demo?delete_users=${deleteUsers}`).then(r => r.data),
  setListingOfMonth: (listing_id, note) => api.post("/admin/listing-of-month", { listing_id, note }).then(r => r.data),
  clearListingOfMonth: () => api.delete("/admin/listing-of-month").then(r => r.data),
  verifications: () => api.get("/admin/verifications").then(r => r.data),
  approveVerification: (uid) => api.post(`/admin/verifications/${uid}/approve`).then(r => r.data),
  rejectVerification: (uid, reason) => api.post(`/admin/verifications/${uid}/reject`, { reason }).then(r => r.data),
  transactions: (params = {}) => {
    // Backend param renamed to `payment_state` to avoid shadowing FastAPI import
    const clean = { ...params };
    if (clean.status) { clean.payment_state = clean.status; delete clean.status; }
    const qs = new URLSearchParams(clean).toString();
    return api.get(`/admin/transactions${qs ? "?" + qs : ""}`).then(r => r.data);
  },
  transactionsSummary: (days = 30) => api.get(`/admin/transactions/summary?days=${days}`).then(r => r.data),
};

// Public curated content
export const featuredAPI = {
  listingOfMonth: () => api.get("/listing-of-month").then(r => r.data),
};

// Business Dashboard
export const businessAPI = {
  dashboard: () => api.get("/business/dashboard").then(r => r.data),
  invoices: () => api.get("/business/invoices").then(r => r.data),
  invoicePdfUrl: (txId) => `${API_URL}/business/invoices/${txId}/pdf`,
  downloadInvoicePdf: async (txId) => {
    const r = await api.get(`/business/invoices/${txId}/pdf`, { responseType: "blob" });
    return r.data;
  },
};

// Payments (Stripe)
export const paymentsAPI = {
  packages: () => api.get("/payments/packages").then(r => r.data),
  createSession: (package_id, listing_id, origin_url) =>
    api.post("/payments/checkout/session", { package_id, listing_id, origin_url }).then(r => r.data),
  createBusinessSubscription: (origin_url, package_id = "business_subscription_year") =>
    api.post("/payments/checkout/session", { package_id, origin_url }).then(r => r.data),
  createBusinessListingFee: (listing_id, origin_url, package_id = "business_listing_fee") =>
    api.post("/payments/checkout/session", { package_id, listing_id, origin_url }).then(r => r.data),
  createAutoListing: (listing_id, origin_url) =>
    api.post("/payments/checkout/session", { package_id: "auto_listing_monthly", listing_id, origin_url }).then(r => r.data),
  status: (session_id) => api.get(`/payments/checkout/status/${session_id}`).then(r => r.data),
};

// Chat
export const chatAPI = {
  start: (listing_id) => api.post("/conversations/start", { listing_id }).then(r => r.data),
  list: () => api.get("/conversations").then(r => r.data),
  get: (cid) => api.get(`/conversations/${cid}`).then(r => r.data),
  messages: (cid) => api.get(`/conversations/${cid}/messages`).then(r => r.data),
  send: (cid, data) => api.post(`/conversations/${cid}/messages`, data).then(r => r.data),
};

// Push
export const pushAPI = {
  vapidKey: () => api.get("/push/vapid-public-key").then(r => r.data),
  subscribe: (sub) => api.post("/push/subscribe", sub).then(r => r.data),
  unsubscribe: (endpoint) => api.delete("/push/subscribe", { data: { endpoint } }).then(r => r.data),
};

// Upload
export const uploadImage = async (file) => {
  const form = new FormData();
  form.append("file", file);
  const r = await api.post("/uploads/image", form, { headers: { "Content-Type": "multipart/form-data" } });
  return r.data.url;
};

export const uploadDocument = async (file) => {
  const form = new FormData();
  form.append("file", file);
  const r = await api.post("/uploads/document", form, { headers: { "Content-Type": "multipart/form-data" } });
  return r.data; // { url, content_type }
};
